import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Bold, Heading2, History, ImagePlus, Italic, Link2, List, RotateCcw, Trash2 } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../shared/ui/feedback/ToastContext';
import { useConfirmationDialog } from '../shared/ui/dialogs/useConfirmationDialog';
import HelpBody from './HelpBody';
import { useHelp } from './HelpContext';
import { createHelpEntry, fetchHelpHistory, removeHelpEntry, saveHelpEntry, uploadHelpImage } from './helpApi';
import { assignableRoles, HELP_ROLES } from './helpRoles';
import { TOURS } from './tours';
import type { HelpEntry, HelpEntryInput, HelpHistoryItem } from './types';

const ROLE_LABEL: Record<string, string> = {
  everyone: 'help.roles.everyone',
  hr: 'help.roles.hr',
  admin: 'help.roles.admin',
  super_admin: 'help.roles.superAdmin',
};

export function slugify(text: string): string {
  return text.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function blank(kind: 'topic' | 'news'): HelpEntryInput {
  return {
    id: '', kind, title: '', summary: null, roles: ['everyone'], routes: [], order: 100, tour: null,
    date: kind === 'news' ? today() : null, body: '',
  };
}

function fromEntry(e: HelpEntry): HelpEntryInput {
  return { id: e.id, kind: e.kind, title: e.title, summary: e.summary, roles: e.roles, routes: e.routes,
    order: e.order, tour: e.tour, date: e.date, body: e.body };
}

type FieldErrors = Record<string, string>;

/** Wraps the textarea's selection in markdown, or inserts at the caret. */
function applyMarkdown(el: HTMLTextAreaElement, before: string, after = '', placeholder = ''): string {
  const { selectionStart: s, selectionEnd: e, value } = el;
  const picked = value.slice(s, e) || placeholder;
  const next = `${value.slice(0, s)}${before}${picked}${after}${value.slice(e)}`;
  window.requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(s + before.length, s + before.length + picked.length);
  });
  return next;
}

/**
 * Editing help inside the panel. The text stays markdown -- the same format the
 * shipped topics are written in -- with a small toolbar for the common marks and a
 * preview that renders exactly as readers will see it. Admins edit any topic; HR
 * edits topics for everyone and for HR (the server enforces both).
 */
export default function HelpEditor({ id, kind }: { id: string | null; kind: 'topic' | 'news' }) {
  const { t, formatDate } = useI18n();
  const toast = useToast();
  const { confirm, confirmationDialog } = useConfirmationDialog();
  const help = useHelp()!;
  const { content } = help.state;
  const { setView, reload, openTopic } = help.actions;
  const uid = useId();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const existing = useMemo(
    () => (id ? [...(content?.topics || []), ...(content?.news || [])].find((x) => x.id === id) || null : null),
    [content, id],
  );
  const [draft, setDraft] = useState<HelpEntryInput>(() => (existing ? fromEntry(existing) : blank(kind)));
  const [initial] = useState(() => JSON.stringify(existing ? fromEntry(existing) : blank(kind)));
  const [idTouched, setIdTouched] = useState(false);
  const [routesText, setRoutesText] = useState(() => (existing?.routes || []).join('\n'));
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState<HelpHistoryItem[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isNew = !id;
  const isNews = draft.kind === 'news';
  const roleChoices = assignableRoles(help.meta.user);

  useEffect(() => {
    if (!historyOpen || !id || history) return;
    fetchHelpHistory(id).then(setHistory).catch(() => setHistory([]));
  }, [historyOpen, id, history]);

  const set = <K extends keyof HelpEntryInput>(key: K, value: HelpEntryInput[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => { if (!e[key as string]) return e; const n = { ...e }; delete n[key as string]; return n; });
  };

  const onTitle = (value: string) => {
    setDraft((d) => ({ ...d, title: value, id: isNew && !idTouched ? slugify(value) : d.id }));
  };

  const toggleRole = (role: string) => {
    const has = draft.roles.includes(role);
    set('roles', has ? draft.roles.filter((r) => r !== role) : [...draft.roles, role]);
  };

  const mark = (before: string, after: string, placeholder: string) => {
    const el = bodyRef.current;
    if (!el) return;
    set('body', applyMarkdown(el, before, after, placeholder));
  };

  const onPickImage = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadHelpImage(file);
      const alt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
      if (bodyRef.current) set('body', applyMarkdown(bodyRef.current, `![${alt}](${url})`, '', ''));
      else set('body', `${draft.body}\n\n![${alt}](${url})`);
    } catch (e: any) {
      setFormError(e?.message || t('help.editor.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const validate = (input: HelpEntryInput): FieldErrors => {
    const out: FieldErrors = {};
    if (!input.title.trim()) out.title = t('help.editor.errors.title');
    if (isNew && !/^[a-z0-9][a-z0-9-]{1,79}$/.test(input.id)) out.id = t('help.editor.errors.id');
    if (!isNews && input.roles.length === 0) out.roles = t('help.editor.errors.roles');
    if (!input.body.trim()) out.body = t('help.editor.errors.body');
    if (isNews && !/^\d{4}-\d{2}-\d{2}$/.test(input.date || '')) out.date = t('help.editor.errors.date');
    if (!isNews && input.routes.some((r) => !r.startsWith('/'))) out.routes = t('help.editor.errors.routes');
    return out;
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const input: HelpEntryInput = {
      ...draft,
      title: draft.title.trim(),
      summary: draft.summary?.trim() || null,
      routes: isNews ? [] : routesText.split(/[\n,]+/).map((r) => r.trim()).filter(Boolean),
      roles: isNews ? ['everyone'] : draft.roles,
      order: Number.isFinite(Number(draft.order)) ? Number(draft.order) : 100,
    };
    const found = validate(input);
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) {
      const first = Object.keys(found)[0];
      document.getElementById(`${uid}-${first}`)?.focus();
      return;
    }
    setSaving(true);
    try {
      if (isNew) await createHelpEntry(input); else await saveHelpEntry(input.id, input);
      await reload();
      toast.success(t('help.editor.saved'));
      if (isNews) setView({ name: 'news' }); else openTopic(input.id);
    } catch (err: any) {
      const fields: FieldErrors = {};
      (err?.payload?.fields || []).forEach((f: any) => { if (f?.field) fields[f.field] = f.message; });
      setErrors(fields);
      setFormError(err?.status === 403 ? t('help.editor.forbidden') : (err?.message || t('help.editor.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!existing) return;
    const reset = existing.source === 'edited';
    const ok = await confirm({
      title: reset ? t('help.editor.resetConfirmTitle') : t('help.editor.deleteConfirmTitle'),
      message: reset ? t('help.editor.resetConfirmBody') : t('help.editor.deleteConfirmBody', { title: existing.title }),
      confirmText: reset ? t('help.editor.reset') : t('help.editor.delete'),
      confirmColor: reset ? undefined : 'error',
    });
    if (!ok) return;
    try {
      await removeHelpEntry(existing.id);
      await reload();
      toast.success(reset ? t('help.editor.resetDone') : t('help.editor.deleted'));
      if (reset && !isNews) openTopic(existing.id);
      else setView(isNews ? { name: 'news' } : { name: 'home' });
    } catch (err: any) {
      setFormError(err?.message || t('help.editor.saveFailed'));
    }
  };

  const cancel = async () => {
    const dirty = JSON.stringify(draft) !== initial || routesText !== (existing?.routes || []).join('\n');
    if (dirty && !(await confirm({
      title: t('help.editor.discardTitle'),
      message: t('help.editor.discardBody'),
      confirmText: t('help.editor.discard'),
      cancelText: t('help.editor.keepEditing'),
    }))) return;
    if (isNews) setView({ name: 'news' });
    else if (id) openTopic(id);
    else setView({ name: 'home' });
  };

  const err = (field: string) => (errors[field]
    ? <p id={`${uid}-${field}-error`} className="khelp-field__error">{errors[field]}</p>
    : null);
  const described = (field: string, hint?: boolean) => [hint ? `${uid}-${field}-hint` : null, errors[field] ? `${uid}-${field}-error` : null].filter(Boolean).join(' ') || undefined;

  if (id && !existing) return <p className="khelp-note">{t('help.topicMissing')}</p>;

  return (
    <form className="khelp-editor" onSubmit={save} noValidate aria-busy={saving}>
      {existing && existing.source !== 'default' && (
        <p className="khelp-meta">{existing.source === 'edited' ? t('help.editor.editedDefault') : t('help.editor.custom')}</p>
      )}

      <div className="khelp-field">
        <label className="khelp-field__label" htmlFor={`${uid}-title`}>{t('help.editor.title')}</label>
        <input id={`${uid}-title`} className="khelp-input" value={draft.title} onChange={(e) => onTitle(e.target.value)}
          maxLength={200} aria-invalid={!!errors.title} aria-describedby={described('title')} autoComplete="off" name="help-title" />
        {err('title')}
      </div>

      {isNew && (
        <div className="khelp-field">
          <label className="khelp-field__label" htmlFor={`${uid}-id`}>{t('help.editor.id')}</label>
          <input id={`${uid}-id`} className="khelp-input khelp-input--mono" value={draft.id}
            onChange={(e) => { setIdTouched(true); set('id', e.target.value.toLowerCase()); }}
            maxLength={80} aria-invalid={!!errors.id} aria-describedby={described('id', true)} autoComplete="off" spellCheck={false} name="help-id" />
          <p id={`${uid}-id-hint`} className="khelp-field__hint">{t('help.editor.idHint')}</p>
          {err('id')}
        </div>
      )}

      {isNews ? (
        <div className="khelp-field">
          <label className="khelp-field__label" htmlFor={`${uid}-date`}>{t('help.editor.date')}</label>
          <input id={`${uid}-date`} type="date" className="khelp-input khelp-input--short" value={draft.date || ''}
            onChange={(e) => set('date', e.target.value)} aria-invalid={!!errors.date} aria-describedby={described('date')} />
          {err('date')}
        </div>
      ) : (
        <>
          <div className="khelp-field">
            <label className="khelp-field__label" htmlFor={`${uid}-summary`}>{t('help.editor.summary')}</label>
            <input id={`${uid}-summary`} className="khelp-input" value={draft.summary || ''} onChange={(e) => set('summary', e.target.value)}
              maxLength={500} aria-describedby={described('summary', true)} autoComplete="off" name="help-summary" />
            <p id={`${uid}-summary-hint`} className="khelp-field__hint">{t('help.editor.summaryHint')}</p>
            {err('summary')}
          </div>

          <fieldset className="khelp-field khelp-fieldset" aria-describedby={described('roles', true)}>
            <legend className="khelp-field__label">{t('help.editor.roles')}</legend>
            <div className="khelp-checks">
              {HELP_ROLES.map((role) => {
                const allowed = roleChoices.includes(role);
                return (
                  <label key={role} className={`khelp-check${allowed ? '' : ' khelp-check--off'}`}>
                    <input type="checkbox" checked={draft.roles.includes(role)} onChange={() => toggleRole(role)} disabled={!allowed} />
                    <span>{t(ROLE_LABEL[role])}</span>
                  </label>
                );
              })}
            </div>
            <p id={`${uid}-roles-hint`} className="khelp-field__hint">
              {roleChoices.includes('admin') ? t('help.editor.rolesHint') : t('help.editor.rolesHintHr')}
            </p>
            {err('roles')}
          </fieldset>

          <div className="khelp-field">
            <label className="khelp-field__label" htmlFor={`${uid}-routes`}>{t('help.editor.routes')}</label>
            <textarea id={`${uid}-routes`} className="khelp-input khelp-input--mono" rows={2} value={routesText}
              onChange={(e) => { setRoutesText(e.target.value); setErrors((x) => { const n = { ...x }; delete n.routes; return n; }); }}
              aria-invalid={!!errors.routes} aria-describedby={described('routes', true)} spellCheck={false} />
            <p id={`${uid}-routes-hint`} className="khelp-field__hint">{t('help.editor.routesHint')}</p>
            {err('routes')}
          </div>

          <div className="khelp-field-row">
            <div className="khelp-field">
              <label className="khelp-field__label" htmlFor={`${uid}-order`}>{t('help.editor.order')}</label>
              <input id={`${uid}-order`} type="number" inputMode="numeric" min={0} max={10000} className="khelp-input khelp-input--short"
                value={draft.order} onChange={(e) => set('order', e.target.value === '' ? 100 : Number(e.target.value))} aria-describedby={described('order')} />
              {err('order')}
            </div>
            <div className="khelp-field">
              <label className="khelp-field__label" htmlFor={`${uid}-tour`}>{t('help.editor.tour')}</label>
              <select id={`${uid}-tour`} className="khelp-input" value={draft.tour || ''} onChange={(e) => set('tour', e.target.value || null)}>
                <option value="">{t('help.editor.noTour')}</option>
                {Object.keys(TOURS).map((tourId) => <option key={tourId} value={tourId}>{t(`help.tours.${tourId}.name`)}</option>)}
              </select>
            </div>
          </div>
        </>
      )}

      <div className="khelp-field">
        <div className="khelp-write-head">
          <span className="khelp-field__label" id={`${uid}-body-label`}>{t('help.editor.body')}</span>
          <div className="khelp-segment" role="group" aria-label={t('help.editor.mode')}>
            <button type="button" className="khelp-segment__btn" aria-pressed={tab === 'write'} onClick={() => setTab('write')}>{t('help.editor.write')}</button>
            <button type="button" className="khelp-segment__btn" aria-pressed={tab === 'preview'} onClick={() => setTab('preview')}>{t('help.editor.preview')}</button>
          </div>
        </div>
        {tab === 'write' ? (
          <>
            <div className="khelp-tools" role="toolbar" aria-label={t('help.editor.formatting')} aria-controls={`${uid}-body`}>
              <button type="button" className="khelp-tool" onClick={() => mark('**', '**', t('help.editor.sample.bold'))} aria-label={t('help.editor.bold')}><Bold size={16} aria-hidden="true" /></button>
              <button type="button" className="khelp-tool" onClick={() => mark('_', '_', t('help.editor.sample.italic'))} aria-label={t('help.editor.italic')}><Italic size={16} aria-hidden="true" /></button>
              <button type="button" className="khelp-tool" onClick={() => mark('\n## ', '\n', t('help.editor.sample.heading'))} aria-label={t('help.editor.heading')}><Heading2 size={16} aria-hidden="true" /></button>
              <button type="button" className="khelp-tool" onClick={() => mark('\n- ', '', t('help.editor.sample.item'))} aria-label={t('help.editor.list')}><List size={16} aria-hidden="true" /></button>
              <button type="button" className="khelp-tool" onClick={() => mark('[', '](/learning)', t('help.editor.sample.link'))} aria-label={t('help.editor.link')}><Link2 size={16} aria-hidden="true" /></button>
              <button type="button" className="khelp-tool" onClick={() => fileRef.current?.click()} aria-label={t('help.editor.image')} disabled={uploading}>
                <ImagePlus size={16} aria-hidden="true" />
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="khelp-visually-hidden" tabIndex={-1}
                aria-hidden="true" onChange={(e) => onPickImage(e.target.files?.[0])} />
              {uploading && <span className="khelp-field__hint" role="status">{t('help.editor.uploading')}</span>}
            </div>
            <textarea ref={bodyRef} id={`${uid}-body`} className="khelp-input khelp-input--body" rows={14} value={draft.body}
              onChange={(e) => set('body', e.target.value)} aria-labelledby={`${uid}-body-label`}
              aria-invalid={!!errors.body} aria-describedby={described('body', true)} name="help-body" />
            <p id={`${uid}-body-hint`} className="khelp-field__hint">{t('help.editor.bodyHint')}</p>
          </>
        ) : (
          <div className="khelp-preview" aria-label={t('help.editor.preview')}>
            {draft.body.trim() ? <HelpBody markdown={draft.body} onOpenTopic={() => {}} /> : <p className="khelp-muted">{t('help.editor.previewEmpty')}</p>}
          </div>
        )}
        {err('body')}
      </div>

      {formError && <p className="khelp-field__error" role="alert">{formError}</p>}

      <div className="khelp-actions">
        <button type="submit" className="khelp-btn khelp-btn--primary" disabled={saving}>{saving ? t('help.editor.saving') : t('help.editor.save')}</button>
        <button type="button" className="khelp-btn khelp-btn--quiet" onClick={cancel}>{t('help.editor.cancel')}</button>
        <span className="khelp-actions__spacer" />
        {existing?.source === 'edited' && (
          <button type="button" className="khelp-btn" onClick={remove}><RotateCcw size={14} aria-hidden="true" />{t('help.editor.reset')}</button>
        )}
        {existing?.source === 'custom' && (
          <button type="button" className="khelp-btn khelp-btn--danger" onClick={remove}><Trash2 size={14} aria-hidden="true" />{t('help.editor.delete')}</button>
        )}
      </div>

      {id && (
        <div className="khelp-history">
          <button type="button" className="khelp-link-btn" aria-expanded={historyOpen} aria-controls={`${uid}-history`} onClick={() => setHistoryOpen((v) => !v)}>
            <History size={14} aria-hidden="true" />{t('help.editor.history')}
          </button>
          {historyOpen && (
            <div id={`${uid}-history`}>
              {!history && <p className="khelp-muted" role="status">{t('help.loading')}</p>}
              {history && history.length === 0 && <p className="khelp-muted">{t('help.editor.historyEmpty')}</p>}
              {history && history.length > 0 && (
                <ol className="khelp-history__list">
                  {history.map((h) => (
                    <li key={h.id} className="khelp-history__item">
                      <span className="khelp-history__what">{t(`help.editor.actions.${h.action}`)}</span>
                      <span className="khelp-history__who">
                        {h.changed_by_name || t('help.editor.someone')} · {formatDate(h.changed_at, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {h.action !== 'reset' && h.action !== 'delete' && h.body !== draft.body && (
                        <button type="button" className="khelp-link-btn" onClick={() => { set('body', h.body); set('title', h.title); setTab('preview'); }}>
                          {t('help.editor.useVersion')}
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      )}
      {confirmationDialog}
    </form>
  );
}
